import { ReactNode } from "react";

// ============= Visit Status Types =============
// UI states that visits can be in (mapped from Blueberry API states)
export const VisitStatus = {
  REQUESTED: "requested", // Maps to Blueberry "SUBMITTED_PENDING"
  ACTIVE: "active", // Client-side state for visits in progress
  COMPLETED: "completed", // Maps to Blueberry "DOC_FINALIZED"
  CANCELLED: "cancelled", // Additional state for cancelled visits
} as const;

export type VisitStatusType = (typeof VisitStatus)[keyof typeof VisitStatus];

// ============= Visit Data Types =============
export interface VisitData {
  id: string;
  patientId?: string;
  patientName?: string;
  patientFirstName?: string;
  patientLastName?: string;
  clinicianName?: string;
  clinicianFirstName?: string;
  clinicianLastName?: string;
  status: VisitStatusType;
  timestamp: string | Date;
  lastMessage?: string;
  hasUnreadMessages?: boolean;
  // Avatar URLs
  patientAvatarUrl?: string;
  clinicianAvatarUrl?: string;
  // Any additional custom data
  metadata?: Record<string, any>;
}

export interface VisitListData {
  current: VisitData[];
  past: VisitData[];
  hasMessagingUnread?: boolean;
}

// ============= Component Props =============
export interface VisitListProps {
  data?: VisitListData;
  isLoading?: boolean;
  error?: Error | null;
  onVisitClick?: (visit: VisitData) => void;
  onNewVisitClick?: () => void;
  showNewVisitButton?: boolean;
  theme?: VisitListTheme;
  className?: string;
  emptyStateComponent?: ReactNode;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  visitItemComponent?: React.ComponentType<VisitListItemProps>;
  newVisitButtonComponent: ReactNode;
  currentSectionTitle?: ReactNode;
  pastSectionTitle?: ReactNode;
}

export interface VisitListItemProps {
  visit: VisitData;
  onClick?: (visit: VisitData) => void;
  showNotificationCard?: boolean;
  isClickable?: boolean;
  theme?: VisitListItemTheme;
  className?: string;
  avatarComponent?: React.ComponentType<AvatarGroupProps>;
  statusComponent?: React.ComponentType<VisitStatusProps>;
}

export interface AvatarGroupProps {
  /** First avatar - can be a URL (starts with http/https) or a name (shows initials). Required. */
  firstAvatar: string;
  /**
   * Second avatar - optional. Can be:
   * - A URL (shows image or data URI)
   * - A name (shows initials)
   * - undefined (hides second avatar entirely - single avatar mode)
   */
  secondAvatar?: string;
  /**
   * Layout variant for avatar arrangement:
   * - horizontal-line: Side by side with animated line separator (pending visits)
   * - horizontal: Overlapping horizontally (active visits)
   * - diagonal: Overlapping diagonally (completed/cancelled visits)
   */
  layout: "horizontal-line" | "horizontal" | "diagonal";
  size?: "small" | "medium" | "large";
  theme?: AvatarGroupTheme;
  className?: string;
}

export interface VisitStatusProps {
  status: VisitStatusType;
  hasUnreadMessages?: boolean;
  timestamp?: string | Date;
  theme?: VisitStatusTheme;
  className?: string;
}

// ============= Theme Types =============
export interface VisitListTheme {
  container?: string;
  header?: {
    title?: string;
    subtitle?: string;
  };
  section?: {
    container?: string;
    title?: string;
    emptyText?: string;
  };
}

export interface VisitListItemTheme {
  container?: {
    base?: string;
    hover?: string;
    active?: string;
    clickable?: string;
  };
  content?: {
    container?: string;
    title?: string;
    subtitle?: string;
  };
  chevron?: string;
  notificationCard?: {
    container?: string;
    icon?: string;
    text?: string;
  };
  avatarGroup?: AvatarGroupTheme;
}

export interface AvatarGroupTheme {
  container?: string;
  avatar?: {
    base?: string;
    first?: string;
    second?: string;
    placeholder?: string;
  };
  separator?: string;
  sizes?: {
    small?: string;
    medium?: string;
    large?: string;
  };
}

export interface VisitStatusTheme {
  container?: string;
  text?: {
    base?: string;
    requested?: string;
    active?: string;
    completed?: string;
    cancelled?: string;
  };
  icon?: {
    base?: string;
    requested?: string;
    active?: string;
    completed?: string;
    cancelled?: string;
  };
  timestamp?: string;
}
